import { useState } from 'react';
import { Button, FileDrop, formatBytes, Pill } from '@elysian/ui';
import { ATTACHMENT_ACCEPT, kindLabel } from '../../data/files';
import type { Attachment, UploadTarget } from '../../data/source';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';

/** Open a stored file in a new tab (bills, receipts) once it has passed the virus scan. */
export async function openFile(blob: Blob | null, notFound: () => void) {
  if (!blob) { notFound(); return; }
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Files kept with a document or journal: receipts, delivery notes, the
 * supplier's PDF. Anyone who can post may add them; they can be taken off a
 * draft, but files on posted records and journals stay (six-year retention).
 */
export function Attachments({ files, target, canAdd, label, onChanged }: {
  files: Attachment[];
  target: Pick<UploadTarget, 'document_id' | 'journal_id'>;
  canAdd: boolean;
  /** "this invoice", "J-0419" */
  label: string;
  onChanged: () => void;
}) {
  const { source } = useSession();
  const toast = useToast((s) => s.show);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function add(list: File[]) {
    setBusy(true);
    setError(null);
    const problems: string[] = [];
    let added = 0;
    for (const f of list) {
      try {
        await source.uploadAttachment(f, { ...target, to_inbox: false });
        added++;
      } catch (e) {
        problems.push(`${f.name}: ${e instanceof Error ? e.message : String(e)}`);
      }
    }
    setBusy(false);
    if (problems.length) setError(problems.join('\n'));
    if (added) { toast(`${added} ${added === 1 ? 'file' : 'files'} attached to ${label}`); onChanged(); }
  }

  async function remove(a: Attachment) {
    setError(null);
    try {
      await source.unlinkAttachment(a.id, target.document_id!);
      toast(`${a.file_name} removed`);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function open(a: Attachment) {
    if (a.scan_status !== 'clean') {
      toast(a.scan_status === 'infected' ? 'This file failed the virus scan and can’t be opened.' : 'This file is still being checked for viruses.');
      return;
    }
    try {
      await openFile(await source.attachmentFile(a.id), () => toast('This demo file has no contents to show.'));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="el-stack" style={{ gap: 10 }}>
      {files.length === 0 && <p className="el-muted" style={{ margin: 0 }}>No files attached.</p>}
      {files.length > 0 && (
        <ul className="el-files" aria-label={`Files on ${label}`}>
          {files.map((a) => (
            <li key={a.id} className="el-file">
              <span className="el-file-kind">{kindLabel(a.content_type)}</span>
              <span className="el-file-name">{a.file_name}<span className="el-hint"> · {formatBytes(a.size_bytes)}</span></span>
              {a.scan_status === 'pending' && <Pill tone="info">Being checked</Pill>}
              {a.scan_status === 'infected' && <Pill tone="danger">Failed virus scan</Pill>}
              <Button size="sm" variant="ghost" onClick={() => void open(a)} aria-label={`Open ${a.file_name}`}>Open</Button>
              {a.can_remove && <Button size="sm" variant="ghost" onClick={() => void remove(a)} aria-label={`Remove ${a.file_name}`}>Remove</Button>}
            </li>
          ))}
        </ul>
      )}
      {canAdd && (
        <FileDrop compact multiple label={busy ? 'Attaching…' : 'Attach files'} disabled={busy} accept={ATTACHMENT_ACCEPT}
          hint="PDFs or photos, up to 25 MB each" onFiles={(f) => void add(f)} />
      )}
      {error && <div className="el-error-box" role="alert" style={{ whiteSpace: 'pre-line' }}>{error}</div>}
    </div>
  );
}
